import React, { useEffect, useState } from "react";
import { Drawer, Form, message, Skeleton, Space } from "antd";
import { SaveOutlined } from "@ant-design/icons";
import CancelButton from "../common/cancelbutton";
import FormButton from "../common/formbutton";
import SortableFormList from "../common/sortableformlist";
import { getPageHighlightsById, savePageHighlights } from "../../services/pagehighlightservice";

interface DrawerHighlightsProps {
    drawerVisible: boolean;
    closeDrawer: () => void;
    isDisabled: boolean;
    pageGuid: string;
    createdBy: string | null;
}

const DrawerHighlights: React.FC<DrawerHighlightsProps> = ({
    drawerVisible,
    closeDrawer,
    isDisabled,
    pageGuid,
    createdBy
}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const handleCloseDrawer = () => {
        form.resetFields();
        closeDrawer();
    };

    useEffect(() => {
        if (drawerVisible) {
            setLoading(true);
            getPageHighlightsById(pageGuid)
                .then(highlights => {
                    form.setFieldsValue({ highlights: highlights.map(h => ({ name: h })) });
                })
                .catch(() => message.error("Failed to load highlights"))
                .finally(() => setLoading(false));
        }
    }, [drawerVisible, pageGuid, form]);

    const handleSave = async () => {
        try {
            setSaving(true);
            const values = form.getFieldsValue();
            const highlights = (values.highlights || []).map((h: any) =>
                typeof h === "string" ? h : h.name
            );

            await savePageHighlights({
                pageGuid,
                createdBy: createdBy ?? "00000000-0000-0000-0000-000000000000",
                highlightSelections: highlights
            });

            message.success("Highlights saved successfully");
            handleCloseDrawer();
        } catch (error) {
            console.error("Save error:", error);
            message.error("Failed to save highlights");
        } finally {
            setSaving(false);
        }
    };

    const footer = (
        <div style={{ textAlign: "right" }}>
            {!isDisabled && (
                <Space>
                    <CancelButton onClick={handleCloseDrawer} />
                    <FormButton
                        title="Save"
                        icon={<SaveOutlined />}
                        onClick={handleSave}
                        loading={saving}
                    />
                </Space>
            )}
        </div>
    );

    return (
        <Drawer
            title="Page Highlights"
            placement="right"
            onClose={handleCloseDrawer}
            open={drawerVisible}
            width={400}
            styles={{
                header: {
                    color: "white",
                    backgroundColor: "var(--color-primary)",
                    fontWeight: 350,
                    fontSize: "14px",
                    border: 0,
                    minHeight: "40px"
                }
            }}
            footer={footer}
        >
            {loading ? (
                <Skeleton active paragraph={{ rows: 4 }} />
            ) : (
                <Form layout="vertical" form={form} style={{ paddingBottom: 80 }}>
                    <SortableFormList
                        name="highlights"
                        label="Highlights"
                        placeholder="Enter Highlight name"
                        form={form}
                        alternateName="Highlight"
                        tooltipText="Use Highlights to showcase key features of a page."
                        disabled={isDisabled}
                    />
                </Form>
            )}
        </Drawer>
    );
};

export default DrawerHighlights;